import React, { useEffect, useState } from "react";
import { Controller, useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";

import Dropdown from "../../../inputs/Dropdown";
import Input from "../../../inputs/Input";
import TextArea from "../../../inputs/TextArea";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import PrimaryButton from "../../../buttons/PrimaryButton";
import DropdownFilter from "../../../inputs/DropdownFilter";
import { listGenderInsurrance } from "../../../../services/microservice/claim/gender/gender-service";
import { useReclamationContext } from "../../../../providers/reclamation/reclamation.provider";
import {
  ICitiesClaim,
  IClientClaim,
  IGenderClaim,
} from "../../../../services/microservice/claim/types/claims";
import { listCityInsurrance } from "../../../../services/microservice/claim/city/city-service";
import { useStepContext } from "../stepper.provider";
import { getMeInsurranceService } from "../../../../services/microservice/claim/auth/auth-service";

interface ICustomStepProps {
  colorPrimary: string;
  dobleAuth?: boolean;
}

type IFormInput = {
  gender: {
    id: number;
    name: string;
  };
  claimCity: {
    id: number;
    nombre: string;
    departamento: {
      nombre: string;
    };
  };
  claimDate: Date;
  claimHour: Date;
  address: string;
  cityResidence: string;
  country: string;
  imeiSecondary: string;
  linePrincipal: string;
  description: string;
};
const messageRequired = " Requerido";
const schemaAditionalInfo = yup
  .object()
  .shape({
    gender: yup.object().required(messageRequired),
    claimCity: yup.object().required(messageRequired),
    claimDate: yup.date().required(messageRequired),
    claimHour: yup.date().required(messageRequired),
    address: yup
      .string()
      .when("$isMobilePhone", (isMobilePhone, schema) =>
        isMobilePhone[0] ? schema.required(messageRequired) : schema.optional(),
      ),
    cityResidence: yup
      .string()
      .when("$isMobilePhone", (isMobilePhone, schema) =>
        isMobilePhone[0] ? schema.required(messageRequired) : schema.optional(),
      ),
    country: yup
      .string()
      .when("$isMobilePhone", (isMobilePhone, schema) =>
        isMobilePhone[0] ? schema.required(messageRequired) : schema.optional(),
      ),
    imeiSecondary: yup
      .string()
      .when(["$requiredImeiSecondary", "$isMobilePhone"], (values, schema) => {
        const requiredImeiSecondary = values[0];
        const isMobilePhone = values[1];

        if (!isMobilePhone) {
          return schema.optional();
        }

        return requiredImeiSecondary
          ? schema
              .matches(/^\d+$/, { message: messageRequired })
              .max(15, "Máximo 15 dígitos")
              .min(15, "Minimo 15 dígitos")
              .required(messageRequired)
          : schema
              .nullable()
              .matches(/^\d+$/, {
                message: messageRequired,
                excludeEmptyString: true,
              })
              .test("maxLength", "Número de 15 dígitos", (val) => {
                console.log(val);
                if (val !== "" && val !== undefined) {
                  if (val?.length === 15 || val?.length === 0) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              })
              .optional();
      }),
    linePrincipal: yup
      .string()
      .matches(/^\d+$/, { message: messageRequired })
      .max(10, "Máximo 10 dígitos")
      .min(10, "Minimo 10 dígitos")
      .required(messageRequired),
    description: yup.string().required(messageRequired),
  })
  .required(messageRequired);
const AditionalStepComponent = ({
  colorPrimary,
  dobleAuth,
}: ICustomStepProps) => {
  const { globalForm, setGlobalForm, createClaimsFormat } =
    useReclamationContext();

  // Detectar si NO es teléfono móvil
  const isMobilePhone =
    globalForm?.device?.dispositivo?.category
      ?.toLowerCase()
      .includes("teléfono") ||
    globalForm?.device?.dispositivo?.category
      ?.toLowerCase()
      .includes("telefono") ||
    globalForm?.device?.dispositivo?.category
      ?.toLowerCase()
      .includes("phone") ||
    globalForm?.device?.dispositivo?.category
      ?.toLowerCase()
      .includes("móvil") ||
    globalForm?.device?.dispositivo?.category?.toLowerCase().includes("movil");

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IFormInput>({
    resolver: yupResolver(schemaAditionalInfo) as any,
    mode: "all",
    context: {
      requiredImeiSecondary: globalForm?.coverage.nombre.includes("Hurto"),
      isMobilePhone,
    },
  });
  const [genders, setGenders] = useState<IGenderClaim[]>();
  const [cities, setCities] = useState<ICitiesClaim[]>();
  const [user, setUser] = useState<IClientClaim>();
  const { setCurrentStep } = useStepContext();
  const actualDate = new Date();

  const onSubmit: SubmitHandler<IFormInput> = (values) => {
    setGlobalForm((prevState: any) => ({ ...prevState, ...values }));
    if (dobleAuth) {
      if (!user) {
        return;
      }
      createClaimsFormat({
        name: user.name,
        secondName: user.second_name,
        identificationNumber: user.identification_number,
        identificationType: user.identification_type.id_system,
        genderName: values.gender.name,
        email: user.email,
        lineOne: globalForm.device.linea_uno,
        addressHome: isMobilePhone ? values.address : "",
        cityHome: isMobilePhone ? values.cityResidence : "",
        countryClaim: isMobilePhone ? values.country : "",
        cityClaim: values.claimCity.nombre,
        lineOneClaim: values.linePrincipal,
        messageTicket: values.description,
        deviceName: globalForm.device.dispositivo.nombre,
        manufacterName: globalForm.device.dispositivo.fabricante,
        imeiOne: globalForm.device.imei_uno,
        imeiSecond: isMobilePhone ? values?.imeiSecondary || "" : "",
        dateClaim: values.claimDate.toISOString(),
        hourClaim: values.claimHour.toISOString(),
      });
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handlePreviusStep = () => {
    setCurrentStep((prev) => prev - 1);
    setGlobalForm((prevState: any) => ({ ...prevState, documents: {} }));
  };

  useEffect(() => {
    listGenderInsurrance().then((res) => setGenders(res.data.cities));
  }, []);
  useEffect(() => {
    const country = !isMobilePhone ? "ECU" : undefined;
    listCityInsurrance(country).then((res) => setCities(res.data.cities));
  }, [isMobilePhone]);

  useEffect(() => {
    getMeInsurranceService().then((res) => setUser(res.data));
  }, []);

  return (
    <section>
      <h2 className="mb-5 text-center text-3xl font-bold">
        4.{" "}
        <span
          style={{
            color: colorPrimary,
          }}
        >
          Información
        </span>{" "}
        adicional
      </h2>
      <p className="my-2 text-base text-critical">* Campos obligatorios</p>
      <div className="mb-10 w-full">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <Dropdown
                options={genders || []}
                label1="name"
                placeholder="Selecciona tu género"
                error={errors.gender?.message}
                required
                {...field}
              />
            )}
          />
          <Controller
            name="claimCity"
            control={control}
            render={({ field }) => (
              <DropdownFilter
                options={cities || []}
                label="nombre"
                placeholder="Selecciona la ciudad en la que ocurrió el evento"
                error={errors.claimCity?.message}
                required
                {...field}
              />
            )}
          />
          <Controller
            name="claimDate"
            control={control}
            render={({ field }) => (
              <div className="my-4 mb-7">
                <div className="h-10">
                  <p className="my-0 py-0 text-base">
                    Selecciona fecha en la que ocurrió el evento{" "}
                    <span className="text-3xl text-critical">*</span>{" "}
                    {errors && (
                      <span className="my-0 py-0 text-base text-critical">
                        {errors.claimDate?.message}
                      </span>
                    )}
                  </p>
                </div>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    sx={{
                      ".css-1ll44ll-MuiOutlinedInput-notchedOutline": {
                        borderRadius: "0.5rem",
                        borderWidth: 0,
                      },
                      ".css-o9k5xi-MuiInputBase-root-MuiOutlinedInput-root": {
                        height: "3rem",
                        paddingTop: "0.75rem",
                        paddingBottom: "0.75rem",
                      },
                      ".css-jupps9-MuiInputBase-root-MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderRadius: "0.5rem",
                          borderWidth: 0,
                        },
                    }}
                    className="w-full rounded-md border-0 bg-gray text-textBlack"
                    label="Selecciona fecha en la que ocurrió el evento"
                    maxDate={dayjs(actualDate)}
                    {...field}
                  />
                </LocalizationProvider>
              </div>
            )}
          />
          <Controller
            name="claimHour"
            control={control}
            render={({ field }) => (
              <div className="my-4 mb-7">
                <div className="h-10">
                  <p className="my-0 py-0 text-base">
                    Selecciona hora en la que ocurrió el evento{" "}
                    <span className="text-3xl text-critical">*</span>{" "}
                    {errors && (
                      <span className="my-0 py-0 text-base text-critical">
                        {errors.claimHour?.message}
                      </span>
                    )}
                  </p>
                </div>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    sx={{
                      ".css-1ll44ll-MuiOutlinedInput-notchedOutline": {
                        borderRadius: "0.5rem",
                        borderWidth: 0,
                      },
                      ".css-o9k5xi-MuiInputBase-root-MuiOutlinedInput-root": {
                        height: "3rem",
                        paddingTop: "0.75rem",
                        paddingBottom: "0.75rem",
                      },
                      ".css-jupps9-MuiInputBase-root-MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderRadius: "0.5rem",
                          borderWidth: 0,
                        },
                    }}
                    className="w-full rounded-md border-0 bg-gray text-textBlack"
                    label="Selecciona hora en la que ocurrió el evento"
                    {...field}
                  />
                </LocalizationProvider>
              </div>
            )}
          />
          {isMobilePhone && (
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  classNameContainer="w-full mb-7"
                  classNameInput="w-full"
                  placeholder="Dirección de residencia"
                  error={errors.address?.message}
                  required
                />
              )}
            />
          )}

          {isMobilePhone && (
            <Controller
              name="cityResidence"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  classNameContainer="w-full mb-7"
                  classNameInput="w-full"
                  placeholder="Ciudad de residencia"
                  error={errors.cityResidence?.message}
                  required
                />
              )}
            />
          )}
          {isMobilePhone && (
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  classNameContainer="w-full mb-7"
                  classNameInput="w-full"
                  placeholder="País donde ocurrió el evento"
                  error={errors.country?.message}
                  required
                />
              )}
            />
          )}
          {isMobilePhone && (
            <Controller
              name="imeiSecondary"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  classNameContainer="w-full mb-7"
                  classNameInput="w-full"
                  placeholder="Número del imei secundario"
                  error={errors.imeiSecondary?.message}
                  required={globalForm?.coverage.nombre.includes("Hurto")}
                />
              )}
            />
          )}
          <Controller
            name="linePrincipal"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                classNameContainer="w-full mb-7"
                classNameInput="w-full"
                placeholder={
                  isMobilePhone
                    ? "Número de línea con la que sucedió el evento"
                    : "Número de línea de contacto"
                }
                error={errors.linePrincipal?.message}
                required
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextArea
                {...field}
                classNameContainer="w-full mb-7"
                classNameInput="w-full"
                placeholder="Descripción del evento"
                error={errors.description?.message}
                rows={4}
                required
              />
            )}
          />
          <div className="mt-4 flex justify-between">
            <PrimaryButton
              title="Atras"
              className="rounded-2xl border px-5 text-textBlack"
              onClick={() => handlePreviusStep()}
              type="button"
            />
            <PrimaryButton
              type="submit"
              title="Siguiente"
              className="rounded-2xl bg-buttonBlack px-5 text-white"
            />
          </div>
        </form>
      </div>
    </section>
  );
};

export default AditionalStepComponent;
